import shortid from 'shortid'

type State = any[]

type Id = string | number

type ViewOptions = {
  id?: Id
}

type Action =
  | { type: 'PUSH'; payload: { component: React.ReactNode; options?: ViewOptions } }
  | { type: 'REPLACE'; payload: { id: Id; component: React.ReactNode; options?: ViewOptions } }
  | { type: 'POP' }
  | { type: 'SET_VIEWS'; payload: { views: any[] } }
  | { type: 'SET_BY_ID'; payload: { id: Id; options: ViewOptions } }
  | { type: 'REMOVE_VIEW_BY_ID'; payload: { id: Id } }
  | { type: 'FILTER'; payload: { filter: (view: any) => Boolean } }

function parseView (component: React.ReactNode, options: ViewOptions = {}) {
  return {
    ...options,
    id: options.id || shortid.generate(),
    component,
  }
}

export default function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'PUSH': {
      const newView = parseView(action.payload.component, action.payload.options)

      const index = state.findIndex(view => view.id === newView.id)

      if (index === -1) {
        return [
          ...state,
          {
            position: state.length > 0 ? state[state.length - 1].position : 'center',
            ...newView
          }
        ]
      } else {
        return [
          ...state.slice(0, index),
          ...state.slice(index + 1),
          {
            position: state.length > 0 ? state[state.length - 1].position : 'center',
            ...newView
          }
        ]
      }
    }
    case 'REPLACE': {
      const index = state.findIndex(view => view.id === action.payload.id)

      if (index === -1) return state

      return [
        ...state.slice(0, index),
        {
          position: state[index].position,
          ...parseView(action.payload.component, {
            ...action.payload.options,
            id: action.payload.options?.id || state[index].id,
          })
        },
        ...state.slice(index + 1)
      ]
    }
    case 'POP':
      return state.length > 0
        ? state.slice(0, -1)
        : state
    case 'SET_VIEWS':
      return action.payload.views.map(view => parseView(view.component, view.options))
    case 'SET_BY_ID': {
      const index = state.findIndex(view => view.id === action.payload.id)

      if (index === -1) return state

      return [
        ...state.slice(0, index),
        {
          ...state[index],
          ...action.payload.options
        },
        ...state.slice(index + 1)
      ]
    }
    case 'REMOVE_VIEW_BY_ID': {
      const index = state.findIndex(view => view.id === action.payload.id)

      if (index === -1) return state

      return [
        ...state.slice(0, index),
      ]
    }
    case 'FILTER': {
      return state.filter(action.payload.filter)
    }
    default: return state
  }
}

export function push (component: React.ReactNode, options?: ViewOptions): Action {
  return {
    type: 'PUSH',
    payload: {
      component,
      options,
    }
  }
}

export function replace (id: Id, component: React.ReactNode, options?: ViewOptions): Action {
  return {
    type: 'REPLACE',
    payload: {
      id,
      component,
      options,
    }
  }
}

export function pop (): Action {
  return {
    type: 'POP',
  }
}

export function setViews (views): Action {
  return {
    type: 'SET_VIEWS',
    payload: {
      views
    }
  }
}

export function set (id: Id, options: ViewOptions): Action {
  return {
    type: 'SET_BY_ID',
    payload: {
      id,
      options,
    }
  }
}

export function removeViewById (id): Action {
  return {
    type: 'REMOVE_VIEW_BY_ID',
    payload: {
      id
    }
  }
}

export function filter (filterFunction: (view: any) => Boolean): Action {
  return {
    type: 'FILTER',
    payload: {
      filter: filterFunction
    }
  }
}
